/**
 * DuplicateAccountsBanner
 *
 * WP-BUG-63. Surfaces accounts that appear to be the same real-world account.
 *
 * Re-linking an institution creates a new provider Item, which issues a new
 * provider account ID. Where the stable identity key (institution + mask +
 * type) is unavailable — Plaid returns mask as optional — matching falls back
 * to that provider ID, so a second row is created for one real account. The
 * result double-counts balances in net worth and retirement projections, and
 * splits transaction history across two accounts.
 *
 * Resolution is always MERGE, never delete: merging transfers transactions,
 * balance anchors, statements and holdings onto the target and soft-deletes
 * the source, whereas deleting a duplicate discards that history permanently.
 */
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import Alert from '@/components/ui/Alert';
import Button from '@/components/ui/Button';
import { useDuplicateAccounts } from '@/hooks/useAccounts';
import { accountsService } from '@/services/accounts';
import type { AccountWithConnection } from '@/services/accounts';
import type { DuplicateAccountGroup } from '@/services/accounts';

export interface DuplicateAccountsBannerProps {
  /** All accounts currently loaded, used to resolve group members to full accounts */
  accounts: AccountWithConnection[];
  /**
   * Opens the merge dialog for one group: `source` is the newer copy (merged
   * FROM, soft-deleted) and `target` the oldest member (kept). Both are
   * members of the reported group, fetched if the page has not loaded them.
   */
  onMergeClick?: (source: AccountWithConnection, target: AccountWithConnection) => void;
  className?: string;
}

export default function DuplicateAccountsBanner({
  accounts,
  onMergeClick,
  className,
}: DuplicateAccountsBannerProps) {
  const { data, isLoading, isError } = useDuplicateAccounts();
  const [resolvingGroupKey, setResolvingGroupKey] = useState<string | null>(null);

  const accountsById = useMemo(() => {
    const map = new Map<string, AccountWithConnection>();
    for (const account of accounts) {
      map.set(account.id, account);
    }
    return map;
  }, [accounts]);

  // The duplicates endpoint reports every non-deleted account, but the page
  // lists active accounts only, so a group member (a closed copy, say) may
  // not be loaded. Fetch it by id rather than substituting another account:
  // merging the wrong account is not undoable (Codex review on PR #11).
  const resolveAccount = (id: string): Promise<AccountWithConnection> => {
    const loaded = accountsById.get(id);
    return loaded ? Promise.resolve(loaded) : accountsService.getById(id);
  };

  const handleMerge = async (group: DuplicateAccountGroup, groupKey: string) => {
    if (!onMergeClick) return;
    // Oldest account is the merge target; newer copies merge into it.
    // group.accounts is ordered oldest-first by the backend.
    const [oldest, newer] = group.accounts;
    if (!oldest || !newer) return;

    setResolvingGroupKey(groupKey);
    try {
      const [source, target] = await Promise.all([
        resolveAccount(newer.id),
        resolveAccount(oldest.id),
      ]);
      onMergeClick(source, target);
    } catch {
      toast.error('Could not load the duplicate accounts. Please try again.');
    } finally {
      setResolvingGroupKey(null);
    }
  };

  // Stay silent while loading, on error, and when there is nothing to report.
  // A detection banner must never be noise on a healthy account list.
  if (isLoading || isError || !data || data.totalGroups === 0) {
    return null;
  }

  const groupLabel = (group: DuplicateAccountGroup) => {
    // account.name is encrypted at rest and decrypted server-side; prefer the
    // loaded account's name, falling back to the mask so the row is still
    // identifiable if the account is not in the current page.
    const first = group.accounts[0];
    const loaded = first ? accountsById.get(first.id) : undefined;
    return loaded?.name ?? `Account ending ${group.mask}`;
  };

  return (
    <Alert
      variant="warning"
      title={
        data.totalGroups === 1
          ? 'Possible duplicate account detected'
          : `${data.totalGroups} possible duplicate accounts detected`
      }
      className={className}
    >
      <p className="text-sm">
        Re-connecting an institution can create a second copy of an account.
        Duplicates double-count balances in your net worth and split
        transaction history. Merging keeps all transactions and history.
      </p>

      <ul className="mt-3 space-y-3">
        {data.groups.map((group) => {
          const groupKey = `${group.institutionId}-${group.mask}-${group.type}`;
          const hasDuplicate = group.accounts.length > 1;

          return (
            <li key={groupKey} className="rounded-md bg-white/60 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {groupLabel(group)}
                  </p>
                  <p className="text-xs text-gray-600">
                    {group.accountCount} copies &middot; ending {group.mask}
                  </p>
                </div>

                {onMergeClick && hasDuplicate && (
                  <Button
                    variant="outline"
                    size="sm"
                    isLoading={resolvingGroupKey === groupKey}
                    disabled={resolvingGroupKey !== null}
                    onClick={() => void handleMerge(group, groupKey)}
                  >
                    Merge
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Alert>
  );
}
